// ikzoekbaas public site — pure, React-free route helpers (unit-testable without a DOM).
//
// The site has two entrances (see publicSite.ts) and therefore two BASES:
//   • its own host      → base ""            : `/`, `/zoek-asieldieren/honden`, `/asieldier/honden/rex`
//   • the path entrance → base "/ikzoekbaas" : the same three, prefixed
//
// Every path this module builds is base-relative, so the same components work on both entrances and a
// deep link keeps the visitor where they already were. Navigation is client-side (history pushState);
// a deep link straight to a search or a detail resolves to it.
//
// The segments mirror the real site (`/zoek-asieldieren/<groep>`, `/asieldier/<groep>/<slug>`) so this
// can stand in for it without every existing link and bookmark breaking.

import { IKZOEKBAAS_PATH_PREFIX, isIkZoekBaasHost } from "./publicSite";

/** The six categories the site splits by. Mirrors the platform `SoortGroep`. */
export const SOORT_GROEPEN = [
  { pad: "honden", label: "Honden" },
  { pad: "katten", label: "Katten" },
  { pad: "konijnen-en-knagers", label: "Konijnen en knagers" },
  { pad: "vogels", label: "Vogels" },
  { pad: "ex-proefdieren", label: "Ex-proefdieren" },
  { pad: "overige", label: "Overige" },
] as const;

export type Route =
  | { view: "home" }
  | { view: "zoek"; groep: string }
  | { view: "detail"; groep: string; slug: string }
  | { view: "opvang"; sleutel: string };

/**
 * The base every ikzoekbaas path hangs off, for the entrance this visitor used. Empty on the site's
 * own host, `/ikzoekbaas` otherwise. Takes hostname + pathname as arguments (not `location`) so it is
 * testable in isolation.
 */
export function siteBase(
  hostname: string | null | undefined,
  pathname: string | null | undefined,
): string {
  if (isIkZoekBaasHost(hostname)) {
    return "";
  }
  return pathname ? IKZOEKBAAS_PATH_PREFIX : "";
}

/** Strip the path entrance, so one parser handles both bases. */
function withoutBase(pathname: string): string {
  const path = pathname.replace(/\/+$/, "");
  return path.startsWith(IKZOEKBAAS_PATH_PREFIX)
    ? path.slice(IKZOEKBAAS_PATH_PREFIX.length)
    : path;
}

function decode(raw: string): string {
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

/** Which page a path resolves to. Anything unrecognised is the home page, never a blank screen. */
export function routeFromPath(pathname: string | null | undefined): Route {
  if (!pathname) {
    return { view: "home" };
  }
  const path = withoutBase(pathname);
  const detail = path.match(/^\/asieldier\/([^/]+)\/(.+)$/);
  if (detail) {
    return { view: "detail", groep: decode(detail[1]), slug: decode(detail[2]) };
  }
  const zoek = path.match(/^\/zoek-asieldieren(?:\/([^/]+))?$/);
  if (zoek) {
    return { view: "zoek", groep: zoek[1] ? decode(zoek[1]) : "" };
  }
  // De eigen pagina van een opvang. De SLEUTEL is het eerste segment en het enige dat oplost; wat
  // erachter staat is de naam, puur voor de leesbaarheid van de link, en wordt genegeerd. Zo blijft
  // een link die op de site van een asiel staat werken als iemand die opvang hernoemt.
  //
  // Twee segmenten en niet "sleutel-naam" in één: de sleutel is een uuid en die zit vol streepjes,
  // dus op een streepje splitsen kan niet.
  const opvang = path.match(/^\/opvang\/([^/]+)(?:\/.*)?$/);
  if (opvang) {
    return { view: "opvang", sleutel: decode(opvang[1]) };
  }
  return { view: "home" };
}

/**
 * Een naam als URL-segment: kleine letters, streepjes, verder niets. Alleen versiering — hij hangt
 * achter de sleutel en niemand leest hem terug.
 */
export function naamSlug(naam: string | null | undefined): string {
  return (naam ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** De deelbare link naar het aanbod van één opvang. */
export function opvangPad(base: string, sleutel: string, naam?: string | null): string {
  const slug = naamSlug(naam);
  return slug
    ? `${base}/opvang/${encodeURIComponent(sleutel)}/${slug}`
    : `${base}/opvang/${encodeURIComponent(sleutel)}`;
}

/** The canonical path for a route on this entrance. */
export function pathFor(base: string, route: Route): string {
  switch (route.view) {
    case "zoek":
      return route.groep
        ? `${base}/zoek-asieldieren/${encodeURIComponent(route.groep)}`
        : `${base}/zoek-asieldieren`;
    case "detail":
      return `${base}/asieldier/${encodeURIComponent(route.groep)}/${encodeURIComponent(route.slug)}`;
    case "opvang":
      return opvangPad(base, route.sleutel);
    default:
      return base === "" ? "/" : base;
  }
}

/** The label for a category path, or the raw path when it is one we do not know. */
export function groepLabel(pad: string): string {
  return SOORT_GROEPEN.find((g) => g.pad === pad)?.label ?? pad;
}
